<template>
  <div :class="wrapperClasses" ref="selectWrapper">
    <label v-if="label" :for="selectId" class="ui-select__label">
      {{ label }}
      <span v-if="required" class="ui-select__required">*</span>
    </label>
    <div class="ui-select__container">
      <div
        :id="selectId"
        :class="selectClasses"
        :tabindex="disabled ? -1 : 0"
        @click="toggleDropdown"
        @blur="handleBlur"
        @focus="handleFocus"
        @keydown.enter.prevent="toggleDropdown"
        @keydown.space.prevent="toggleDropdown"
        @keydown.esc="isOpen = false"
      >
        <Icon
          v-if="leadingIcon"
          :icon="leadingIcon"
          :class="['ui-select__leading-icon', leadingIconClass]"
        />
        <span v-if="displayValue" class="ui-select__value">
          <template v-if="formatter">
            <span v-html="formatter(selectedRaw)"></span>
          </template>
          <template v-else>
            <Icon
              v-if="selectedNormalized?.icon"
              :icon="selectedNormalized.icon"
              :style="selectedNormalized.iconColor ? { color: selectedNormalized.iconColor } : {}"
              class="ui-select__value-icon"
            />
            {{ displayValue }}
          </template>
        </span>
        <span v-else class="ui-select__placeholder">{{ placeholder }}</span>
        <div v-if="loading" class="ui-select__spinner">
          <Icon icon="mdi:loading" class="ui-select__spinner-icon" />
        </div>
        <button
          v-if="clearable && displayValue && !disabled"
          type="button"
          class="ui-select__clear"
          @click.stop="clearValue"
          aria-label="Clear selection"
        >
          <Icon icon="mdi:close-circle" />
        </button>
        <div class="ui-select__icon" :class="{ 'is-open': isOpen }">
          <Icon icon="mdi:chevron-down" />
        </div>
      </div>
      <Teleport to="body" v-if="isMounted">
        <Transition name="dropdown">
          <div
            v-if="isOpen && (normalizedOptions.length > 0 || searchable)"
            ref="dropdownMenu"
            class="ui-select__dropdown"
            :style="dropdownStyle"
          >
            <div v-if="searchable" class="ui-select__search">
              <Icon icon="mdi:magnify" class="ui-select__search-icon" />
              <input
                ref="searchInput"
                type="text"
                v-model="searchQuery"
                :placeholder="searchPlaceholder"
                class="ui-select__search-input"
                @click.stop
              />
            </div>
            <div class="ui-select__options-list">
              <template
                v-for="item in groupedItems"
                :key="item._groupHeader ? `header-${item.group}` : item.value"
              >
                <div v-if="item._groupHeader" class="ui-select__group-header">
                  {{ item.group }}
                </div>
                <div
                  v-else
                  class="ui-select__option"
                  :class="{ 'is-selected': isSelected(item), 'is-disabled': item.disabled }"
                >
                  <div class="ui-select__option-content" @click="handleSelectOption(item)">
                    <template v-if="formatter">
                      <span
                        class="ui-select__option-label"
                        v-html="formatter(item._raw || item)"
                      ></span>
                    </template>
                    <template v-else>
                      <Icon
                        v-if="item.icon"
                        :icon="item.icon"
                        :style="item.iconColor ? { color: item.iconColor } : {}"
                        class="ui-select__option-icon"
                      />
                      <span class="ui-select__option-label">{{ item.label }}</span>
                    </template>
                    <span
                      v-if="item.description"
                      class="ui-select__info-icon"
                      :class="{ 'ui-select__info-icon--hover': infoTooltipTrigger === 'hover' }"
                      @click.stop="
                        infoTooltipTrigger === 'click' ? toggleInfoTooltip(item.value) : undefined
                      "
                    >
                      <Icon icon="mdi:information-outline" width="14" height="14" />
                      <span
                        v-if="infoTooltipTrigger === 'hover' || activeInfoTooltip === item.value"
                        class="ui-select__info-tooltip"
                      >
                        {{ item.description }}
                      </span>
                    </span>
                    <Icon v-if="isSelected(item)" icon="mdi:check" class="ui-select__check-icon" />
                  </div>
                  <div v-if="showActions" class="ui-select__option-actions">
                    <button
                      v-if="canEdit"
                      @click.stop="
                        isOpen = false;
                        emit('edit', item._raw || item);
                      "
                      class="ui-select__action-btn ui-select__action-btn--edit"
                      type="button"
                      aria-label="Edit"
                    >
                      <Icon icon="mdi:pencil-outline" width="14" height="14" />
                    </button>
                    <button
                      v-if="canDelete"
                      @click.stop="
                        isOpen = false;
                        emit('delete', item._raw || item);
                      "
                      class="ui-select__action-btn ui-select__action-btn--delete"
                      type="button"
                      aria-label="Delete"
                    >
                      <Icon icon="mdi:trash-can-outline" width="14" height="14" />
                    </button>
                  </div>
                </div>
              </template>
              <div v-if="filteredOptions.length === 0" class="ui-select__no-results">
                {{ noResultsText }}
              </div>
            </div>
            <div v-if="showAddButton" class="ui-select__footer">
              <button
                @click.stop="emit('add-new')"
                class="ui-select__add-btn"
                type="button"
                aria-label="Add new"
              >
                <Icon icon="mdi:plus" width="14" height="14" />
                <span>{{ addButtonText }}</span>
              </button>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
    <div v-if="error || helperText" class="ui-select__helper">
      <span v-if="error" class="ui-select__error">{{ error }}</span>
      <span v-else-if="helperText" class="ui-select__helper-text">{{ helperText }}</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Icon } from "@iconify/vue";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import type { SelectOption, Size } from "./types";

interface Props {
  modelValue?: any;
  label?: string;
  placeholder?: string;
  options: SelectOption[] | any[];
  size?: Size;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  helperText?: string;
  id?: string;
  leadingIcon?: string;
  leadingIconClass?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  noResultsText?: string;
  clearable?: boolean;
  labelKey?: string;
  valueKey?: string;
  returnObject?: boolean;
  formatter?: Function;
  showActions?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  showAddButton?: boolean;
  addButtonText?: string;
  loading?: boolean;
  infoTooltipTrigger?: "click" | "hover";
}

const props = withDefaults(defineProps<Props>(), {
  size: "md",
  disabled: false,
  required: false,
  searchable: false,
  searchPlaceholder: "Search...",
  noResultsText: "No results found",
  clearable: false,
  returnObject: false,
  showActions: false,
  canEdit: true,
  canDelete: true,
  showAddButton: false,
  addButtonText: "Add New",
  loading: false,
  infoTooltipTrigger: "click",
});

const emit = defineEmits<{
  "update:modelValue": [value: any];
  blur: [event: FocusEvent];
  focus: [event: FocusEvent];
  change: [value: any];
  edit: [option: any];
  delete: [option: any];
  "add-new": [];
}>();

const isFocused = ref(false);
const isOpen = ref(false);
const isMounted = ref(false);
const selectWrapper = ref<HTMLElement | null>(null);
const dropdownMenu = ref<HTMLElement | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);
const dropdownStyle = ref<Record<string, string>>({});
const searchQuery = ref("");
const activeInfoTooltip = ref<any>(null);

const toggleInfoTooltip = (value: any) => {
  activeInfoTooltip.value = activeInfoTooltip.value === value ? null : value;
};

const selectId = computed(() => props.id || `select-${Math.random().toString(36).substr(2, 9)}`);

// Normalize options: handles both {value, label} arrays and raw object arrays with labelKey/valueKey
const normalizedOptions = computed<(SelectOption & { _raw?: any })[]>(() => {
  const opts = Array.isArray(props.options) ? props.options : [];
  if (!opts.length) return [];

  // If labelKey/valueKey are provided, map raw objects
  if (props.labelKey && props.valueKey) {
    return opts.map((opt) => {
      if (typeof opt === "object" && opt !== null) {
        return {
          value: opt[props.valueKey!],
          label: String(opt[props.labelKey!] ?? ""),
          disabled: opt.disabled,
          icon: opt.icon,
          iconColor: opt.iconColor,
          group: opt.group,
          description: opt.description,
          _raw: opt,
        };
      }
      return { value: opt, label: String(opt), _raw: opt };
    });
  }

  // Standard SelectOption[] — pass through
  return opts.map((opt) => ({ ...opt, _raw: opt }));
});

const filteredOptions = computed(() => {
  if (!props.searchable || !searchQuery.value) {
    return normalizedOptions.value;
  }
  const query = searchQuery.value.toLowerCase();
  return normalizedOptions.value.filter((option) => option.label.toLowerCase().includes(query));
});

// Build grouped items: inserts group headers before each new group
const groupedItems = computed(() => {
  const items: any[] = [];
  let lastGroup: string | undefined = undefined;
  for (const opt of filteredOptions.value) {
    const group = opt.group || opt._raw?.group;
    if (group && group !== lastGroup) {
      items.push({ _groupHeader: true, group });
      lastGroup = group;
    }
    items.push(opt);
  }
  return items;
});

// Find the selected normalized option
const selectedNormalized = computed(() => {
  const mv = props.modelValue;
  if (mv == null) return null;

  // If returnObject or modelValue is an object, match by valueKey or value
  if (typeof mv === "object" && mv !== null && props.valueKey) {
    const mvVal = mv[props.valueKey];
    return normalizedOptions.value.find((opt) => opt.value === mvVal) || null;
  }

  return normalizedOptions.value.find((opt) => opt.value === mv) || null;
});

// The raw object for the selected option (for formatter)
const selectedRaw = computed(() => {
  return selectedNormalized.value?._raw || props.modelValue;
});

// Display text for the selected option
const displayValue = computed(() => {
  if (selectedNormalized.value) return selectedNormalized.value.label;
  // If modelValue is an object and we have a labelKey, read it directly
  if (props.modelValue && typeof props.modelValue === "object" && props.labelKey) {
    return props.modelValue[props.labelKey] || null;
  }
  return null;
});

const wrapperClasses = computed(() => {
  const classes = ["ui-select"];
  classes.push(`ui-select--${props.size}`);
  if (props.disabled) classes.push("ui-select--disabled");
  if (props.error) classes.push("ui-select--error");
  return classes;
});

const selectClasses = computed(() => {
  const classes = ["ui-select__field"];
  if (isFocused.value || isOpen.value) classes.push("ui-select__field--focused");
  if (isOpen.value) classes.push("ui-select__field--open");
  if (props.disabled) classes.push("ui-select__field--disabled");
  if (props.leadingIcon) classes.push("ui-select__field--with-leading-icon");
  return classes;
});

const isSelected = (option: SelectOption & { _raw?: any }) => {
  const mv = props.modelValue;
  if (mv == null) return false;

  // Object modelValue — match by valueKey
  if (typeof mv === "object" && mv !== null && props.valueKey) {
    return option.value === mv[props.valueKey!];
  }

  return option.value === mv;
};

const toggleDropdown = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    nextTick(() => {
      updateDropdownPosition();
      if (props.searchable && searchInput.value) {
        searchInput.value.focus();
      }
    });
  }
};

const handleSelectOption = (option: SelectOption & { _raw?: any }) => {
  if (option.disabled) return;
  const emitValue = props.returnObject ? option._raw || option : option.value;
  emit("update:modelValue", emitValue);
  emit("change", emitValue);
  isOpen.value = false;
  activeInfoTooltip.value = null;
};

const clearValue = () => {
  emit("update:modelValue", null);
  emit("change", null);
};

const updateDropdownPosition = () => {
  if (!selectWrapper.value || !dropdownMenu.value) return;

  try {
    const rect = selectWrapper.value.getBoundingClientRect();
    if (!rect) return;

    const spacing = 4;
    const dropdownHeight = dropdownMenu.value.offsetHeight || 200;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const shouldShowAbove = spaceBelow < dropdownHeight + spacing && spaceAbove > spaceBelow;

    dropdownStyle.value = {
      position: "fixed",
      top: shouldShowAbove
        ? `${rect.top - dropdownHeight - spacing}px`
        : `${rect.bottom + spacing}px`,
      left: `${rect.left}px`,
      minWidth: `${rect.width}px`,
      width: "auto",
      zIndex: "10090",
    };
  } catch (error) {
    console.warn("Error updating dropdown position:", error);
  }
};

const handleClickOutside = (event: MouseEvent) => {
  if (
    selectWrapper.value &&
    !selectWrapper.value.contains(event.target as Node) &&
    dropdownMenu.value &&
    !dropdownMenu.value.contains(event.target as Node)
  ) {
    isOpen.value = false;
  }
};

const handleBlur = (event: FocusEvent) => {
  isFocused.value = false;
  emit("blur", event);
};

const handleFocus = (event: FocusEvent) => {
  isFocused.value = true;
  emit("focus", event);
};

watch(isOpen, (newVal) => {
  if (newVal) {
    nextTick(() => {
      if (selectWrapper.value && dropdownMenu.value) {
        updateDropdownPosition();
      }
    });
  } else {
    searchQuery.value = "";
  }
});

onMounted(() => {
  nextTick(() => {
    if (typeof document !== "undefined" && document.body) {
      isMounted.value = true;
    }
  });
  document.addEventListener("mousedown", handleClickOutside);
  window.addEventListener(
    "scroll",
    () => {
      if (isOpen.value && selectWrapper.value && dropdownMenu.value) {
        updateDropdownPosition();
      }
    },
    true
  );
  window.addEventListener("resize", () => {
    if (isOpen.value && selectWrapper.value && dropdownMenu.value) {
      updateDropdownPosition();
    }
  });
});

onUnmounted(() => {
  document.removeEventListener("mousedown", handleClickOutside);
  window.removeEventListener("scroll", updateDropdownPosition, true);
  window.removeEventListener("resize", updateDropdownPosition);
});
</script>

<style scoped>
.ui-select {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.ui-select__label {
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: #344054;
  margin-bottom: 6px;
}

.ui-select__required {
  color: var(--color-danger);
  font-size: 1.1rem;
  margin-left: 2px;
}

.ui-select__container {
  position: relative;
  display: flex;
  align-items: center;
}

.ui-select__field {
  width: 100%;
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  font-weight: 400;
  color: var(--color-text);
  background-color: var(--color-bg-white);
  border: 1px solid var(--color-border);
  border-radius: calc(var(--radius) - 2px);
  padding: 10px 40px 10px 12px;
  transition: all var(--transition-normal);
  height: var(--input-height-md);
  cursor: pointer;
  display: flex;
  align-items: center;
  position: relative;
}

.ui-select__field--with-leading-icon {
  padding-left: 36px !important;
}

.ui-select--sm .ui-select__field--with-leading-icon {
  padding-left: 32px !important;
}

.ui-select--lg .ui-select__field--with-leading-icon {
  padding-left: 40px !important;
}

.ui-select__leading-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 16px;
  width: 16px;
  height: 16px;
  z-index: 1;
  pointer-events: none;
  flex-shrink: 0;
}

.ui-select__value {
  color: var(--color-text);
  font-weight: 400;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}

.ui-select__value-icon {
  font-size: 15px;
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}

.ui-select__placeholder {
  color: var(--color-text-muted);
  flex: 1;
}

.ui-select__field:hover:not(.ui-select__field--disabled) {
  border-color: var(--color-border-hover);
}

.ui-select__field--focused,
.ui-select__field:focus {
  outline: none !important;
  border-color: var(--color-primary) !important;
  box-shadow: var(--ring) !important;
}

.ui-select__field--disabled {
  background-color: var(--color-bg-disabled);
  cursor: not-allowed;
  opacity: 0.5;
  border-color: var(--color-border-light);
}

.ui-select__clear {
  position: absolute;
  right: 32px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  color: var(--color-text-muted);
  font-size: 16px;
  transition: color var(--transition-fast);
  z-index: 1;
}

.ui-select__clear:hover {
  color: var(--color-text-placeholder);
}

.ui-select__spinner {
  position: absolute;
  right: 32px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.ui-select__spinner-icon {
  font-size: 16px;
  color: var(--color-text-muted);
  animation: ui-select-spin 1s linear infinite;
}

@keyframes ui-select-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.ui-select__icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: var(--color-text-placeholder);
  font-size: 1.25rem;
  transition: transform var(--transition-normal);
  flex-shrink: 0;
}

.ui-select__icon.is-open {
  transform: translateY(-50%) rotate(180deg);
  color: var(--color-primary);
}

.ui-select__field--open .ui-select__icon {
  transform: translateY(-50%) rotate(180deg);
  color: var(--color-primary);
}

.ui-select__helper {
  margin-top: var(--space-1);
  font-size: var(--font-size-xs);
}

.ui-select__error {
  color: var(--color-danger);
}

.ui-select__helper-text {
  color: var(--color-text-placeholder);
}

/* Size: sm */
.ui-select--sm .ui-select__label {
  font-size: 12px;
}

.ui-select--sm .ui-select__field {
  height: var(--input-height-sm);
  padding: 8px 36px 8px 10px;
  font-size: var(--font-size-xs);
}

.ui-select--sm .ui-select__icon {
  font-size: 1rem;
}

/* Size: lg */
.ui-select--lg .ui-select__label {
  font-size: 13px;
}

.ui-select--lg .ui-select__field {
  height: var(--input-height-lg);
  padding: 12px 44px 12px 16px;
  font-size: 0.9rem;
}

.ui-select--lg .ui-select__icon {
  font-size: 1.5rem;
}

/* Error state */
.ui-select--error .ui-select__field {
  border-color: var(--color-danger);
}

.ui-select--error .ui-select__field:focus,
.ui-select--error .ui-select__field--focused {
  border-color: var(--color-danger) !important;
  box-shadow: var(--ring-danger) !important;
}

/* Dropdown */
.ui-select__dropdown {
  background: var(--color-bg-white);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius);
  box-shadow: var(--shadow-md);
  max-height: 300px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: var(--space-1) 0 var(--space-1) var(--space-1);
  margin-top: var(--space-1);
}

.ui-select__search {
  position: relative;
  padding: 6px var(--space-1);
  border-bottom: 1px solid var(--color-border-light);
  flex-shrink: 0;
}

.ui-select__search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-placeholder);
  font-size: 1.1rem;
  pointer-events: none;
}

.ui-select__search-input {
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 1px solid var(--color-border);
  border-radius: calc(var(--radius) - 2px);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--color-text);
  background-color: var(--color-bg-white);
  transition: all var(--transition-normal);
}

.ui-select__search-input:focus {
  outline: none !important;
  border-color: var(--color-primary) !important;
  box-shadow: var(--ring) !important;
}

.ui-select__options-list {
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
}

.ui-select__no-results {
  padding: 20px 12px;
  text-align: center;
  color: var(--color-text-placeholder);
  font-size: var(--font-size-sm);
}

.ui-select__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: calc(var(--radius) - 2px);
  transition: background-color var(--transition-fast);
  font-size: var(--font-size-sm);
  color: #344054;
  font-weight: 400;
}

.ui-select__option-content {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: var(--space-2) var(--space-3);
  flex: 1;
  cursor: pointer;
  min-width: 0;
}

.ui-select__option:hover:not(.is-disabled) {
  background-color: var(--color-bg-hover);
  color: var(--color-primary);
}

.ui-select__option.is-selected {
  background-color: var(--color-bg-selected);
  color: #344054;
  font-weight: 400;
}

.ui-select__option.is-disabled {
  color: var(--color-text-muted);
  cursor: not-allowed;
  opacity: 0.6;
}

.ui-select__check-icon {
  font-size: 16px;
  color: var(--color-primary);
  flex-shrink: 0;
  width: 16px;
  height: 16px;
}

.ui-select__option-icon {
  font-size: 15px;
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  margin-right: var(--space-2);
}

.ui-select__option-label {
  flex: 1;
  white-space: nowrap;
}

.ui-select__group-header {
  padding: 6px 12px 4px;
  font-size: 10px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  pointer-events: none;
  user-select: none;
}

.ui-select__group-header:not(:first-child) {
  margin-top: 4px;
  border-top: 1px solid var(--color-border-light);
  padding-top: 8px;
}

.ui-select__info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 2px;
  position: relative;
}

.ui-select__info-icon:hover {
  color: var(--color-primary);
}

.ui-select__info-tooltip {
  position: absolute;
  right: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  background: #1e293b;
  color: #fff;
  font-size: 12px;
  line-height: 1.4;
  padding: 6px 10px;
  border-radius: 6px;
  white-space: normal;
  width: 220px;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  pointer-events: none;
}

.ui-select__info-icon--hover .ui-select__info-tooltip {
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.15s ease,
    visibility 0.15s ease;
}

.ui-select__info-icon--hover:hover .ui-select__info-tooltip {
  opacity: 1;
  visibility: visible;
}

/* Action buttons (edit/delete per option) */
.ui-select__option-actions {
  display: flex;
  align-items: center;
  gap: 0;
  margin-left: var(--space-1);
  padding-right: var(--space-2);
}

.ui-select__action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: calc(var(--radius) - 2px);
  cursor: pointer;
  transition: all var(--transition-normal);
  background: transparent;
  padding: 0;
  margin: 0;
}

.ui-select__action-btn:hover {
  transform: scale(1.1);
}

.ui-select__action-btn--edit {
  color: var(--color-primary);
}

.ui-select__action-btn--edit:hover {
  background-color: #ececff;
}

.ui-select__action-btn--delete {
  color: #ff0f0f;
}

.ui-select__action-btn--delete:hover {
  background-color: #ffe5e5;
}

.ui-select__action-btn :deep(svg) {
  width: 14px;
  height: 14px;
}

/* Add New footer */
.ui-select__footer {
  padding: var(--space-2);
  border-top: 1px solid var(--color-border-light);
  flex-shrink: 0;
}

.ui-select__add-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: var(--space-2) var(--space-3);
  background: transparent;
  border: 1px dashed var(--color-primary);
  border-radius: calc(var(--radius) - 2px);
  color: var(--color-primary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-normal);
  gap: 6px;
}

.ui-select__add-btn:hover {
  background-color: var(--color-bg-hover);
  border-style: solid;
}

.ui-select__add-btn :deep(svg) {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

/* Dropdown transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all var(--transition-normal);
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Scrollbar styling */
.ui-select__options-list::-webkit-scrollbar {
  width: 6px;
}

.ui-select__options-list::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: var(--radius);
}

.ui-select__options-list::-webkit-scrollbar-thumb {
  background: var(--color-border);
  border-radius: var(--radius);
}

.ui-select__options-list::-webkit-scrollbar-thumb:hover {
  background: var(--color-text-muted);
}
</style>
